import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Toggle from "react-toggle";
import { LockIcon, Search } from "lucide-react";
import ReactSelect from "react-select";
import { useApolloClient, useQuery } from "@apollo/client";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/ui/page-header";
import { TableHeader, TableBody, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  StyledTableShell,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  EmptyState,
} from "@/components/ui/styled-table";
import { StatusBadge } from "@/components/ui/table-components";
import PublicCandidateDetailModal from "./PublicCandidateDetailModal";
import {
  PublicCandidate,
  PublicCandidatesData,
  PartnerCandidatesData,
  ProjectCandidate,
  ProjectCandidatesData,
  CommunityCandidatesData,
  LocalityData,
  ProjectCandidateBoards,
} from "../../interfaces";
import CustomPagination, {
  CustomPaginationType,
} from "../Global/CustomPagination";
import { GET_LOCALITIES, GET_PROFILE } from "../../GraphQL/settings/queries";
import {
  GET_PUBLIC_CANDIDATES,
  GET_PARTNER_CANDIDATES,
  GET_PROJECT_CANDIDATES,
  GET_COMMUNITY_CANDIDATES,
  GET_PROJECT_CANDIDATE_BOARDS,
  GET_CANDIDATE_BY_ID,
} from "../../GraphQL/candidates/queries";
import {
  DesktopContainer,
  MobileContainer,
  MobileCard,
  SearchContainer,
  PageContainer,
  AdvancedSearchContainer,
  AdvancedSearchButton,
  ClearSummarySearch,
  InputSearchGroup,
  ClearSearchButton,
  ToggleIndustryWrapper,
  TextIndustryWrapper,
} from "../../sharedStyles";
import AdvancedSearch from "../AdvancedSearch";
import useCheckMobile from "../../utils/hooks/useCheckMobile";
import { useFeatureFlagEnabled } from "posthog-js/react";
import config from "../../config";

import guide from "../../assets/role_relay_user_guide.pdf";
import DemoProjects from "../DemoProjects";

const candidateStatusLabels: Record<string, string> = {
  PLATFORM_INCLUDED: "Pending Opt-in",
  SHARED: "Shared",
  STOPPED_SHARING: "Draft",
  REMOVED: "Removed",
  UNKNOWN: "Unknown",
};

const boardStatusLabels: Record<string, string> = {
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
  OPEN: "Open",
};

interface Props {
  user: any;
  initialActiveTab?: string;
}

const PublicCandidates = ({ user, initialActiveTab = "public" }: Props) => {
  const { data: profileData, loading: profileLoading } = useQuery(GET_PROFILE);
  const shouldShowOnlyCommunityTabs = profileData?.profile?.company?.settings?.features?.communityOnly;
  const getAdvancedSearchStorage = window?.localStorage?.getItem(
    "advancedSearchCandidates"
  );
  const searchStorage = getAdvancedSearchStorage
    ? JSON.parse(getAdvancedSearchStorage ?? "")
    : "";
  const client = useApolloClient();
  const navigate = useNavigate();

  const showAdvancedSearchFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showAdvancedSearch
  );

  const showDemoProjectsFlag = useFeatureFlagEnabled(
    config?.postHog?.featureFlags?.showDemoProjects
  );

  const [tabKey, setTabKey] = useState(initialActiveTab);
  const [showCandidateDetailModal, setShowCandidateDetailModal] =
    useState(false);
  const [activeCandidate, setActiveCandidate] = useState(null as any);

  const [publicCandidates, setPublicCandidates] = useState(
    [] as PublicCandidate[]
  );
  const [partnerCandidates, setPartnerCandidates] = useState(
    [] as PublicCandidate[]
  );
  const [projectCandidates, setProjectCandidates] = useState(
    [] as ProjectCandidate[]
  );
  const [communityCandidates, setCommunityCandidates] = useState(
    [] as PublicCandidate[]
  );

  /**
   * Logic for deeplinked candidate
   */
  const [routeCandidateId, setRouteCandidateId] = useState<number | null>(null);
  const { candidateId } = useParams<{
    candidateId: string;
  }>();

  useEffect(() => {
    candidateId
      ? setRouteCandidateId(Number.parseInt(candidateId))
      : setRouteCandidateId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (routeCandidateId !== null && !showCandidateDetailModal) {
    if (tabKey === "partner" || tabKey === "project") {
      const candidate = partnerCandidates.find(
        (candidate) => candidate.id === routeCandidateId
      );
      if (candidate) {
        setActiveCandidate(candidate);
        setShowCandidateDetailModal(true);
      }
    } else if (tabKey === "community") {
      const candidate = communityCandidates.find(
        (candidate) => candidate.id === routeCandidateId
      );
      if (candidate) {
        setActiveCandidate(candidate);
        setShowCandidateDetailModal(true);
      }
    } else {
      const candidate = publicCandidates.find(
        (candidate) => candidate.id === routeCandidateId
      );
      if (candidate) {
        setActiveCandidate(candidate);
        setShowCandidateDetailModal(true);
      }
    }

    //TODO: handle if role not found
  }

  const [projectBoards, setProjectBoards] = useState(
    [] as ProjectCandidateBoards[]
  );
  const [filterIndustry, setFilterIndustry] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showSummarySearchPublic, setShowSummarySearchPublic] = useState(
    searchStorage?.publicCandidatesStatus ?? false
  );
  const [summarySearchContentPublic, setSummarySearchContentPublic] =
    useState<string>(searchStorage?.publicCandidatesSummary ?? "");
  const [filterPublicQuery, setFilterPublicQuery] = useState(
    searchStorage?.publicCandidatesFilter ?? {}
  );
  const [showSummarySearchPartner, setShowSummarySearchPartner] = useState(
    searchStorage?.partnerCandidatesStatus ?? false
  );
  const [summarySearchContentPartner, setSummarySearchContentPartner] =
    useState(searchStorage?.partnerCandidatesSummary ?? "");
  const [filterPartnerQuery, setFilterPartnerQuery] = useState(
    searchStorage?.partnerCandidatesFilter ?? {}
  );

  const [boardId, setBoardId] = useState<string | null>(null);
  const [boardOptions, setBoardOptions] = useState<any>(null);

  const [hasNextPagePublic, setHasNextPagePublic] = useState(false);
  const [hasNextPagePartner, setHasNextPagePartner] = useState(false);
  const [hasNextPageProject, setHasNextPageProject] = useState(false);
  const [hasNextPageCommunity, setHasNextPageCommunity] = useState(false);

  const publicPaginationRef = useRef<CustomPaginationType>(null);
  const partnerPaginationRef = useRef<CustomPaginationType>(null);
  const projectPaginationRef = useRef<CustomPaginationType>(null);
  const communityPaginationRef = useRef<CustomPaginationType>(null);

  const { width } = useCheckMobile();
  const isMobile = width < 960;

  // Set default tab to community when communityOnly is true
  useEffect(() => {
    if (shouldShowOnlyCommunityTabs && tabKey === initialActiveTab) {
      setTabKey("community");
    }
  }, [shouldShowOnlyCommunityTabs, tabKey, initialActiveTab]);

  const { data: dataLocalities } = useQuery<LocalityData>(GET_LOCALITIES);
  const localities = dataLocalities?.localities?.items
    ? [...dataLocalities?.localities?.items]
    : [];
  const locationName = (x: string): string[] => {
    return localities.map((locality) =>
      x === locality?.name ? `${locality?.name} (${locality?.state})` : ""
    );
  };

  const { data: publicCandidatesData } = useQuery<PublicCandidatesData>(
    GET_PUBLIC_CANDIDATES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPublicQuery,
        filterByPreferences: filterIndustry,
      },
      onCompleted: (data) => {
        setHasNextPagePublic(data?.publicCandidates?.pageInfo?.hasNextPage);
        setPublicCandidates(data?.publicCandidates?.items);
      },
    }
  );

  const { data: partnerCandidatesData } = useQuery<PartnerCandidatesData>(
    GET_PARTNER_CANDIDATES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPartnerQuery,
      },
      onCompleted: (data) => {
        setHasNextPagePartner(data?.partnerCandidates?.pageInfo?.hasNextPage);
        setPartnerCandidates(data?.partnerCandidates?.items);
      },
    }
  );

  const { data: communityCandidatesData } = useQuery<CommunityCandidatesData>(
    GET_COMMUNITY_CANDIDATES,
    {
      variables: {
        search: searchTerm,
        skip: 0,
        take: 25,
        where: filterPartnerQuery,
      },
      onCompleted: (data) => {
        setHasNextPageCommunity(
          data?.communityCandidates?.pageInfo?.hasNextPage
        );
        setCommunityCandidates(data?.communityCandidates?.items);
      },
    }
  );

  const {
    error: errorProjectBoards,
    loading: loadingProjectBoards,
    data: dataProjectBoards,
  } = useQuery(GET_PROJECT_CANDIDATE_BOARDS);

  useEffect(() => {
    setProjectBoards(dataProjectBoards?.projectCandidateBoards?.items);
    if (dataProjectBoards?.projectCandidateBoards?.items?.length) {
      setBoardOptions(
        dataProjectBoards?.projectCandidateBoards?.items?.map((brd: any) => ({
          value: brd.id,
          label: brd.title,
        }))
      );
      setBoardId(dataProjectBoards?.projectCandidateBoards?.items[0].id);
    }
  }, [dataProjectBoards]);

  const runSearch = (skip = 0, take = 25) => {
    console.log("running search with tab ", tabKey);
    if (tabKey === "public") {
      const getPublicCandidates = async () => {
        const { data } = await client.query({
          query: GET_PUBLIC_CANDIDATES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPublicQuery,
            filterByPreferences: filterIndustry,
          },
        });
        setPublicCandidates(data.publicCandidates?.items);
        publicPaginationRef.current?.setPagination({
          ...data?.publicCandidates?.pageInfo,
          skip,
          take,
          totalCount: data?.publicCandidates?.totalCount,
        });
      };
      getPublicCandidates();
    } else if (tabKey === "partner") {
      // partner
      const getPartnerCandidates = async () => {
        const { data } = await client.query({
          query: GET_PARTNER_CANDIDATES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPartnerQuery,
          },
        });

        setPartnerCandidates(data.partnerCandidates?.items);

        partnerPaginationRef.current?.setPagination({
          ...data?.partnerCandidates?.pageInfo,
          skip,
          take,
          totalCount: data?.partnerCandidates?.totalCount,
        });
      };
      getPartnerCandidates();
    } else if (tabKey === "community") {
      // community
      const getCommunityCandidates = async () => {
        const { data } = await client.query({
          query: GET_COMMUNITY_CANDIDATES,
          variables: {
            search: searchTerm,
            skip,
            take,
            where: filterPartnerQuery,
          },
        });

        setCommunityCandidates(data.communityCandidates?.items);

        communityPaginationRef.current?.setPagination({
          ...data?.communityCandidates?.pageInfo,
          skip,
          take,
          totalCount: data?.communityCandidates?.totalCount,
        });
      };
      getCommunityCandidates();
    } else if (tabKey === "project" && boardId !== null) {
      // project
      console.group("getting project candidates");
      const getProjectCandidates = async () => {
        const { data } = await client.query({
          query: GET_PROJECT_CANDIDATES,
          variables: {
            projectCandidateBoardId: boardId,
            skip,
            take,
            where: filterPartnerQuery,
          },
        });

        setProjectCandidates(data.projectCandidates?.items);

        projectPaginationRef.current?.setPagination({
          ...data?.projectCandidates?.pageInfo,
          skip,
          take,
          totalCount: data?.projectCandidates?.totalCount,
        });
      };
      getProjectCandidates();
    }
  };

  useEffect(() => {
    setSearchTerm("");
    runSearch();
  }, [tabKey]);

  useEffect(() => {
    if (searchTerm === "" && tabKey !== "project") runSearch();
  }, [searchTerm]);

  useEffect(() => {
    runSearch();
  }, [
    publicCandidatesData?.publicCandidates?.items,
    partnerCandidatesData?.partnerCandidates?.items,
    communityCandidatesData?.communityCandidates?.items,
    boardId,
  ]);

  const selectCandidate = (ind: number) => {
    if (tabKey === "partner") {
      setActiveCandidate(partnerCandidates[ind]);
      navigate(`/partner-candidates/${partnerCandidates[ind].id}`);
    } else if (tabKey === "project") {
      setActiveCandidate(projectCandidates[ind]);
      navigate(`/project-candidates/${projectCandidates[ind].id}`);
    } else if (tabKey === "community") {
      setActiveCandidate(communityCandidates[ind]);
      navigate(`/community-candidates/${communityCandidates[ind].id}`);
    } else {
      setActiveCandidate(publicCandidates[ind]);
      navigate(`/public-candidates/${publicCandidates[ind].id}`);
    }
    setShowCandidateDetailModal(true);
  };

  const setActiveTab = (tab: string | null) => {
    if (tab) {
      setTabKey(tab);
    }
    setFilterPublicQuery(searchStorage?.publicCandidatesFilter);
    setFilterPartnerQuery(searchStorage?.partnerCandidatesFilter);
  };

  const handleOpenAdvancedSearch = () => {
    setShowAdvancedSearch((val) => !val);
  };

  const handleProjectBoardChange = (board: any) => {
    console.log("setting board id", board.value);
    setBoardId(board.value);
  };

  const handleClearSummarySearch = () => {
    if (searchStorage) {
      if (tabKey === "public") {
        searchStorage.publicCandidatesStatus = false;
        searchStorage.publicCandidatesSummary = "";
        searchStorage.publicCandidatesFilter = {};
        setShowSummarySearchPublic(false);
        setFilterPublicQuery({});
      } else {
        searchStorage.partnerCandidatesStatus = false;
        searchStorage.partnerCandidatesSummary = "";
        searchStorage.partnerCandidatesFilter = {};
        setShowSummarySearchPartner(false);
        setFilterPartnerQuery({});
      }
      window.localStorage.setItem(
        "advancedSearchCandidates",
        JSON.stringify(searchStorage)
      );
    }
  };

  if (profileLoading) {
    return (
      <PageContainer>
        <div>Loading...</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {showDemoProjectsFlag && <DemoProjects />}
      <PublicCandidateDetailModal
        variantType={tabKey}
        show={showCandidateDetailModal}
        candidate={activeCandidate}
        refetch={runSearch}
        boardId={boardId}
        onHide={() => {
          let baseUrl = `/public-candidates`;
          if (tabKey === "partner") {
            baseUrl = `/partner-candidates`;
          } else if (tabKey === "community") {
            baseUrl = `/community-candidates`;
          } else if (tabKey === "project") {
            baseUrl = `/project-candidates`;
          }
          window.history.replaceState(null, "", baseUrl);
          setRouteCandidateId(null);

          setShowCandidateDetailModal(false);
        }}
      />
      <AdvancedSearchContainer>
        {showAdvancedSearch && (
          <AdvancedSearch
            searchType={tabKey}
            isMobile={isMobile}
            onHandleModal={() => setShowAdvancedSearch(false)}
            onHandleSearch={
              tabKey === "public"
                ? (data: PublicCandidate[]) => setPublicCandidates(data)
                : (data: PublicCandidate[]) => setPartnerCandidates(data)
            }
            onShowSummary={
              tabKey === "public"
                ? (data: boolean) => setShowSummarySearchPublic(data)
                : (data: boolean) => setShowSummarySearchPartner(data)
            }
            onHandleSummaryContent={
              tabKey === "public"
                ? (content: string) => setSummarySearchContentPublic(content)
                : (content: string) => setSummarySearchContentPartner(content)
            }
            onHandleFilterQuery={
              tabKey === "public"
                ? (queryData: any) => setFilterPublicQuery(queryData)
                : (queryData: any) => setFilterPartnerQuery(queryData)
            }
          />
        )}
      </AdvancedSearchContainer>
      <PageHeader title="Open Candidates" subtitle="Find talent" />
      <Tabs value={tabKey} onValueChange={setActiveTab}>
        <TabsList>
          {!shouldShowOnlyCommunityTabs && (
            <TabsTrigger value="public">Public Candidates</TabsTrigger>
          )}
          {!shouldShowOnlyCommunityTabs && communityCandidates.length > 0 && (
            <TabsTrigger value="community">Community Candidates</TabsTrigger>
          )}
          {partnerCandidates.length > 0 && (
            <TabsTrigger value="partner">Partner Candidates</TabsTrigger>
          )}
          {projectBoards !== undefined && projectBoards.length > 0 && (
            <TabsTrigger value="project">Project Candidates</TabsTrigger>
          )}
        </TabsList>
        {!shouldShowOnlyCommunityTabs && (
          <TabsContent value="public" style={!isMobile ? { position: "relative" } : {}}>
          <StyledTableShell>
            <CardContent>
              <SearchContainer screen={!isMobile}>
                <InputSearchGroup screen={!isMobile}>
                  <Input
                    className={isMobile ? "text-lg" : ""}
                    type="text"
                    value={searchTerm}
                    placeholder="Search by job title, industry or location"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") runSearch();
                    }}
                  />
                  {searchTerm.length > 0 && (
                    <ClearSearchButton
                      size={25}
                      screen={!isMobile}
                      onClick={() => setSearchTerm("")}
                    />
                  )}
                  <Button onClick={() => runSearch()}>
                    <Search className="mr-2 h-4 w-4" /> Search
                  </Button>
                </InputSearchGroup>
                {showAdvancedSearchFlag && (
                  <AdvancedSearchButton
                    screen={!isMobile}
                    onClick={handleOpenAdvancedSearch}
                  >
                    Advanced Search
                  </AdvancedSearchButton>
                )}
              </SearchContainer>
              {isMobile ? <hr /> : ""}
              {showSummarySearchPublic && (
                <p>
                  {summarySearchContentPublic}
                  <ClearSummarySearch onClick={handleClearSummarySearch}>
                    clear search
                  </ClearSummarySearch>
                </p>
              )}
              <DesktopContainer>
                <ToggleIndustryWrapper screen={!isMobile}>
                  <Toggle
                    checked={filterIndustry}
                    icons={false}
                    onChange={() => setFilterIndustry((x) => !x)}
                  />
                  <TextIndustryWrapper>
                    Filter by my{" "}
                    <Link to={"/settings"} state={{ byIndustry: true }}>
                      industry preferences
                    </Link>
                  </TextIndustryWrapper>
                </ToggleIndustryWrapper>
                <StyledTable>
                  <TableHeader>
                    <TableRow>
                      <StyledTableHead>Job Titles</StyledTableHead>
                      <StyledTableHead>Industry</StyledTableHead>
                      <StyledTableHead>Location</StyledTableHead>
                      <StyledTableHead>Type</StyledTableHead>
                      <StyledTableHead className="text-center">Status</StyledTableHead>
                      <StyledTableHead>Exp. (yrs)</StyledTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {publicCandidates.length === 0 && (
                      <TableRow>
                        <StyledTableCell colSpan={6}>
                          <EmptyState>No open candidates available.</EmptyState>
                        </StyledTableCell>
                      </TableRow>
                    )}
                    {publicCandidates.map((candidate, ind) => (
                      <StyledTableRow key={candidate.id} onClick={() => selectCandidate(ind)} style={{ cursor: "pointer" }}>
                        <StyledTableCell>{candidate.idealJobTitles}</StyledTableCell>
                        <StyledTableCell>{candidate.industry}</StyledTableCell>
                        <StyledTableCell>
                          {locationName(candidate?.location?.displayName)}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Badge variant="secondary">
                            {candidate.workType === "TEMP"
                              ? "CONTRACT/TEMP"
                              : candidate.workType}
                          </Badge>
                        </StyledTableCell>
                        <StyledTableCell className="text-center">
                          <StatusBadge status={candidateStatusLabels[candidate?.status] || "Unknown"} />
                        </StyledTableCell>
                        <StyledTableCell>{candidate.yearsOfExperience}</StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </DesktopContainer>
              <MobileContainer>
                <ToggleIndustryWrapper screen={!isMobile}>
                  <Toggle
                    checked={filterIndustry}
                    icons={false}
                    onChange={() => setFilterIndustry((x) => !x)}
                  />
                  <TextIndustryWrapper>
                    Filter by my{" "}
                    <Link to={"/settings"} state={{ byIndustry: true }}>
                      industry preferences
                    </Link>
                  </TextIndustryWrapper>
                </ToggleIndustryWrapper>
                {publicCandidates.map((candidate, ind) => (
                  <MobileCard
                    key={candidate.id}
                    onClick={() => selectCandidate(ind)}
                  >
                    <div>
                      <h4>{candidate.idealJobTitles}</h4>
                      <strong>Industry:</strong> {candidate.industry}
                      <br />
                      <strong>Location:</strong>{" "}
                      {locationName(candidate?.location?.displayName)}
                      <br />
                    </div>
                    <div>
                      <Badge variant="secondary">
                        {candidate.workType === "TEMP"
                          ? "CONTRACT/TEMP"
                          : "PERMANENT"}
                      </Badge>
                      <div>Exp: {candidate.yearsOfExperience} yrs</div>
                    </div>
                  </MobileCard>
                ))}
              </MobileContainer>
              {hasNextPagePublic && (
                <CustomPagination
                  ref={publicPaginationRef}
                  onPaginationChange={(args) => runSearch(args.skip, args.take)}
                />
              )}
              <p>
                <a href={guide} target="_blank" rel="noreferrer">
                  Click here to view the User Guide.
                </a>
              </p>
            </CardContent>
          </StyledTableShell>
        </TabsContent>
        )}
        {(shouldShowOnlyCommunityTabs || communityCandidates.length > 0) && (
          <TabsContent value="community" style={!isMobile ? { position: "relative" } : {}}>
            <StyledTableShell>
              <CardContent>
                {/* <SearchContainer screen={!isMobile}>
                <InputSearchGroup screen={!isMobile}>
                  <Input
                    className={isMobile ? "text-lg" : ""}
                    type="text"
                    value={searchTerm}
                    placeholder="Search by job title, industry or location"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") runSearch();
                    }}
                  />
                  {searchTerm.length > 0 && (
                    <ClearSearchButton
                      size={25}
                      screen={!isMobile}
                      onClick={() => setSearchTerm("")}
                    />
                  )}
                  <Button onClick={() => runSearch()}>
                    <Search /> Search
                  </Button>
                </InputSearchGroup>
                {showAdvancedSearchFlag && (
                  <AdvancedSearchButton
                    screen={!isMobile}
                    onClick={handleOpenAdvancedSearch}
                  >
                    Advanced Search
                  </AdvancedSearchButton>
                )}
              </SearchContainer>
              {isMobile ? <hr /> : ""} 
              {showSummarySearchPublic && (
                <p>
                  {summarySearchContentPublic}
                  <ClearSummarySearch onClick={handleClearSummarySearch}>
                    clear search
                  </ClearSummarySearch>
                </p>
              )}*/}
                <DesktopContainer>
                  {/* <ToggleIndustryWrapper screen={!isMobile}>
                  <Toggle
                    checked={filterIndustry}
                    icons={false}
                    onChange={() => setFilterIndustry((x) => !x)}
                  />
                  <TextIndustryWrapper>
                    Filter by my{" "}
                    <Link to={"/settings"} state={{ byIndustry: true }}>
                      industry preferences
                    </Link>
                  </TextIndustryWrapper>
                </ToggleIndustryWrapper> */}
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Job Titles</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead>Agency</StyledTableHead>
                        <StyledTableHead>Exp. (yrs)</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {communityCandidates.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={6}>
                            <EmptyState>No open candidates available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}

                      {communityCandidates.map((candidate, ind) => {
                        //magic check to determine if candidate is anonymous - if companyName is null or empty, we consider it anonymous
                        const isAnonymous = !candidate.ownerCompanyName;

                        return (
                          <StyledTableRow key={candidate.id} onClick={() => selectCandidate(ind)} style={{ cursor: "pointer" }}>
                            <StyledTableCell>{candidate.idealJobTitles}</StyledTableCell>
                            <StyledTableCell>{candidate.industry}</StyledTableCell>
                            <StyledTableCell>
                              {locationName(candidate?.location?.displayName)}
                            </StyledTableCell>
                            <StyledTableCell>
                              <Badge variant="secondary">
                                {candidate.workType === "TEMP" ? "CONTRACT/TEMP" : candidate.workType}
                              </Badge>
                            </StyledTableCell>
                            
                            
                            <StyledTableCell>
                              {isAnonymous ? (
                                <span className="flex items-center gap-1 text-slate-500 italic">
                                  <LockIcon className="w-3 h-3" /> Confidential
                                </span>
                              ) : (
                                <span className="font-medium text-teal-600">
                                  {candidate.ownerCompanyName}
                                </span>
                              )}
                            </StyledTableCell>

                            <StyledTableCell>{candidate.yearsOfExperience}</StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  <ToggleIndustryWrapper screen={!isMobile}>
                    <Toggle
                      checked={filterIndustry}
                      icons={false}
                      onChange={() => setFilterIndustry((x) => !x)}
                    />
                    <TextIndustryWrapper>
                      Filter by my{" "}
                      <Link to={"/settings"} state={{ byIndustry: true }}>
                        industry preferences
                      </Link>
                    </TextIndustryWrapper>
                  </ToggleIndustryWrapper>
                  
                  {communityCandidates.map((candidate, ind) => {
                    const isAnonymous = !candidate.ownerCompanyName;
                    return (
                      <MobileCard
                        key={candidate.id}
                        onClick={() => selectCandidate(ind)}
                      >
                        <div>
                          <h4>{candidate.idealJobTitles}</h4>
                          <strong>Industry:</strong> {candidate.industry}
                          <br />
                          <strong>Location:</strong>{" "}
                          {locationName(candidate?.location?.displayName)}
                          <br />
                          <strong>Agency:</strong>{" "}
                          {isAnonymous ? (
                            <span className="text-slate-500 italic">
                              <LockIcon className="inline w-3 h-3 mr-1" />Confidential
                            </span>
                          ) : (
                            <span className="font-medium text-teal-600">
                              {candidate.ownerCompanyName}
                            </span>
                          )}
                        </div>
                        <div>
                          <Badge variant="secondary">
                            {candidate.workType === "TEMP"
                              ? "CONTRACT/TEMP"
                              : "PERMANENT"}
                          </Badge>
                          <div>Exp: {candidate.yearsOfExperience} yrs</div>
                        </div>
                      </MobileCard>
                    );
                  })}
                </MobileContainer>
                {hasNextPageCommunity && (
                  <CustomPagination
                    ref={communityPaginationRef}
                    onPaginationChange={(args) =>
                      runSearch(args.skip, args.take)
                    }
                  />
                )}
                <p>
                  <a href={guide} target="_blank" rel="noreferrer">
                    Click here to view the User Guide.
                  </a>
                </p>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
        {partnerCandidates.length > 0 && (
          <TabsContent
            value="partner"
            
            style={!isMobile ? { position: "relative" } : {}}
          >
            <StyledTableShell>
              <CardContent>
                <SearchContainer screen={!isMobile}>
                  <InputSearchGroup screen={!isMobile}>
                    <Input
                      className={isMobile ? "text-lg" : ""}
                      type="text"
                      value={searchTerm}
                      placeholder="Search by job title, industry or location"
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyUp={(e) => {
                        if (e.key === "Enter") runSearch();
                      }}
                    />
                    {searchTerm.length > 0 && (
                      <ClearSearchButton
                        size={25}
                        screen={!isMobile}
                        onClick={() => setSearchTerm("")}
                      />
                    )}
                    <Button onClick={() => runSearch()}>
                      <Search /> Search
                    </Button>
                  </InputSearchGroup>
                  {showAdvancedSearchFlag && (
                    <AdvancedSearchButton
                      screen={!isMobile}
                      onClick={handleOpenAdvancedSearch}
                    >
                      Advanced Search
                    </AdvancedSearchButton>
                  )}
                </SearchContainer>
                {isMobile ? <hr /> : ""}
                {showSummarySearchPartner && (
                  <p>
                    {summarySearchContentPartner}
                    <ClearSummarySearch onClick={handleClearSummarySearch}>
                      clear search
                    </ClearSummarySearch>
                  </p>
                )}
                <DesktopContainer>
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Job Titles</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead className="text-center">Status</StyledTableHead>
                        <StyledTableHead>Agency</StyledTableHead>
                        <StyledTableHead>Years Experience</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partnerCandidates.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={7}>
                            <EmptyState>No partner candidates available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {partnerCandidates.map((candidate, ind) => (
                        <StyledTableRow key={candidate.id} onClick={() => selectCandidate(ind)} style={{ cursor: "pointer" }}>
                          <StyledTableCell>{candidate.idealJobTitles}</StyledTableCell>
                          <StyledTableCell>{candidate.industry}</StyledTableCell>
                          <StyledTableCell>
                            {locationName(candidate?.location?.displayName)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Badge variant="secondary">
                              {candidate.workType === "TEMP"
                                ? "CONTRACT/TEMP"
                                : candidate.workType}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell className="text-center">
                            <StatusBadge status={candidateStatusLabels[candidate?.status] || "Unknown"} />
                          </StyledTableCell>
                          <StyledTableCell>{candidate.companyName}</StyledTableCell>
                          <StyledTableCell>{candidate.yearsOfExperience}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  {partnerCandidates.map((candidate, ind) => (
                    <MobileCard
                      key={candidate.id}
                      onClick={() => selectCandidate(ind)}
                    >
                      <div>
                        <h4>{candidate.idealJobTitles}</h4>
                        <strong>Industry:</strong> {candidate.industry}
                        <br />
                        <strong>Location:</strong>{" "}
                        {locationName(candidate?.location?.displayName)}
                        <br />
                        <strong>Agency:</strong> {candidate.companyName}
                        <br />
                      </div>
                      <div>
                        <Badge variant="secondary">
                          {candidate.workType === "TEMP"
                            ? "CONTRACT/TEMP"
                            : "PERMANENT"}
                        </Badge>
                        <div>Exp: {candidate.yearsOfExperience} yrs</div>
                      </div>
                    </MobileCard>
                  ))}
                </MobileContainer>
                {hasNextPagePartner && (
                  <CustomPagination
                    ref={partnerPaginationRef}
                    onPaginationChange={(args) =>
                      runSearch(args.skip, args.take)
                    }
                  />
                )}
                <p>
                  <a href={guide} target="_blank" rel="noreferrer">
                    Click here to view the User Guide.
                  </a>
                </p>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
        {projectBoards !== undefined && projectBoards.length > 0 && (
          <TabsContent
            value="project"
            
            style={!isMobile ? { position: "relative" } : {}}
          >
            <StyledTableShell>
              <CardContent>
                <div className="mb-3">
                  {loadingProjectBoards && <span>Loading...</span>}
                  {!loadingProjectBoards &&
                    !errorProjectBoards &&
                    projectBoards?.length && (
                      <ReactSelect
                        options={boardOptions}
                        onChange={handleProjectBoardChange}
                        defaultValue={boardOptions.filter(
                          (option: any) => option.value === boardId
                        )}
                      />
                      // <Dropdown onChange={handleProjectBoardChange}>
                      //   <Dropdown.Toggle variant="primary" id="dropdown-basic">
                      //     Select Projects
                      //   </Dropdown.Toggle>
                      //   <Dropdown.Menu>
                      //     {projectBoards?.map(({ id, title }) => (
                      //       <Dropdown.Item key={id} href="#">
                      //         {title}
                      //       </Dropdown.Item>
                      //     ))}
                      //   </Dropdown.Menu>
                      // </Dropdown>
                    )}
                </div>
                <DesktopContainer>
                  <StyledTable>
                    <TableHeader>
                      <TableRow>
                        <StyledTableHead>Name</StyledTableHead>
                        <StyledTableHead>Job Title</StyledTableHead>
                        <StyledTableHead>Industry</StyledTableHead>
                        <StyledTableHead>Location</StyledTableHead>
                        <StyledTableHead>Type</StyledTableHead>
                        <StyledTableHead className="text-center">Status</StyledTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectCandidates.length === 0 && (
                        <TableRow>
                          <StyledTableCell colSpan={6}>
                            <EmptyState>No project candidates available.</EmptyState>
                          </StyledTableCell>
                        </TableRow>
                      )}
                      {projectCandidates.map((candidate, ind) => (
                        <StyledTableRow key={candidate?.id} onClick={() => selectCandidate(ind)} style={{ cursor: "pointer" }}>
                          <StyledTableCell>
                            {candidate?.firstName} {candidate.lastName}
                          </StyledTableCell>
                          <StyledTableCell>{candidate?.idealJobTitles}</StyledTableCell>
                          <StyledTableCell>{candidate?.industry}</StyledTableCell>
                          <StyledTableCell>
                            {locationName(candidate?.location?.displayName)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Badge variant="secondary">
                              {candidate?.workType === "TEMP"
                                ? "CONTRACT/TEMP"
                                : "PERMANENT"}
                            </Badge>
                          </StyledTableCell>
                          <StyledTableCell className="text-center">
                            <StatusBadge status={boardStatusLabels[candidate?.boardStatus] || candidate?.boardStatus || "Unknown"} />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </StyledTable>
                </DesktopContainer>
                <MobileContainer>
                  {projectCandidates.map((candidate, ind) => (
                    <MobileCard
                      key={candidate?.id}
                      onClick={() => selectCandidate(ind)}
                    >
                      <div>
                        <h4>{candidate?.idealJobTitles}</h4>
                        <strong>Industry:</strong> {candidate?.industry}
                        <br />
                        <strong>Location:</strong>{" "}
                        {locationName(candidate?.location?.displayName)}
                        <br />
                        <strong>Agency:</strong> {candidate?.companyName}
                        <br />
                      </div>
                      <div>
                        <Badge variant="secondary">
                          {candidate?.workType === "TEMP"
                            ? "CONTRACT/TEMP"
                            : "PERMANENT"}
                        </Badge>
                        <div>Exp: {candidate?.yearsOfExperience} yrs</div>
                      </div>
                    </MobileCard>
                  ))}
                </MobileContainer>
              </CardContent>
            </StyledTableShell>
          </TabsContent>
        )}
      </Tabs>
    </PageContainer>
  );
};

export default PublicCandidates;
